import {
  isAssetReadOnly,
  Media,
  useCurrentAccount,
  useProduct,
  useWorkspaceAsset,
} from '@gripp/shared-logic';
import {
  LoadingIndicator,
  MediaListItem,
  useSlideLayoutSafe,
} from '@gripp/shared-ui';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import { ScrollView } from 'react-native-gesture-handler';
import { useNavigate } from 'react-router-dom';
import { useAssetRouting } from '../assetRouteContext';
import { AddAssetMediaType, AddFilesLinksSheet } from './addFilesLinksButton';
import { FilesContext, MediaExtended } from './filesContext';
import { filesSharedStyles } from './filesSharedStyles';

type FileRightColumnProps = {
  assetId: string;
  searchKeyword: string;
  selectedCategoryId: string | undefined;
  onAllFilesLoaded: (files: MediaExtended[]) => void;
  onFilteredCountChange?: (count: number) => void;
  issueId?: string;
};

export const FileRightColumn = ({
  assetId,
  issueId,
  searchKeyword,
  selectedCategoryId,
  onAllFilesLoaded,
  onFilteredCountChange,
}: FileRightColumnProps) => {
  const { workspaceAsset, invalidateQuery, isLoading } = useWorkspaceAsset(assetId);
  const { basePath } = useAssetRouting(assetId);
  const asset = workspaceAsset?.asset;
  const { account } = useCurrentAccount();
  const isReadOnlyMode = isAssetReadOnly(account, workspaceAsset);
  const navigate = useNavigate();
  const slideLayout = useSlideLayoutSafe();
  const { isAddSheetOpen, setIsAddSheetOpen } = useContext(FilesContext);
  // Lifted from AddFilesLinksSheet so we can keep the sheet mounted while
  // the upload Modal (rendered via Portal by the sheet) is visible, even
  // after the bottom sheet itself has closed.
  const [selectedType, setSelectedType] = useState<AddAssetMediaType | null>(null);
  const isSheetActive = isAddSheetOpen || !!selectedType;

  // Render the add sheet as a column overlay so its backdrop covers the
  // PanelHeader too (the inline sheet only darkens the body area). The
  // overlay is only registered while actually needed — otherwise the
  // columnOverlay wrapper would capture clicks on the right column.
  useEffect(() => {
    if (!slideLayout || !asset || isReadOnlyMode) return;
    if (!isSheetActive) {
      slideLayout.setColumnOverlay(null);
      return;
    }
    slideLayout.setColumnOverlay(
      <AddFilesLinksSheet
        asset={asset}
        invalidateQuery={invalidateQuery}
        isOpen={isAddSheetOpen}
        onClose={() => setIsAddSheetOpen(false)}
        selectedType={selectedType}
        onSelectedTypeChange={setSelectedType}
      />
    );
    return () => slideLayout.setColumnOverlay(null);
  }, [
    slideLayout,
    asset,
    isReadOnlyMode,
    invalidateQuery,
    isAddSheetOpen,
    isSheetActive,
    selectedType,
    setIsAddSheetOpen,
  ]);

  const { product } = useProduct(asset?.product?.id || '');

  const allFiles = useMemo(() => {
    const files: MediaExtended[] = [];

    const productMediaIds = new Set(
      product?.media?.map((m: Media) => m.id) || []
    );

    if (asset?.media) {
      asset.media.forEach((media: Media) => {
        const isFromProduct = productMediaIds.has(media.id);
        files.push({ ...media, isFromProduct });
      });
    }

    onAllFilesLoaded(files);
    return files;
  }, [asset?.media, product?.media, onAllFilesLoaded]);

  const filteredFiles = useMemo(() => {
    let files = allFiles;

    if (selectedCategoryId) {
      files = files.filter((file) =>
        file.categories?.some((cat) => cat.id === selectedCategoryId)
      );
    }

    if (searchKeyword.trim()) {
      const query = searchKeyword.toLowerCase();
      files = files.filter((file) => file.name?.toLowerCase().includes(query));
    }

    return files;
  }, [allFiles, searchKeyword, selectedCategoryId]);

  useEffect(() => {
    onFilteredCountChange?.(filteredFiles.length);
  }, [filteredFiles.length, onFilteredCountChange]);

  const handleMediaClick = useCallback(
    (media: Media) => {
      const url = issueId
        ? `/activities/${issueId}/asset/${assetId}/files/${media.id}`
        : `${basePath}/files/${media.id}`;

      // Pass source so AssetFilePreview knows to show the file list in the
      // left panel (current panel stays as the navigation context).
      navigate(url, {
        state: { source: 'filesList' },
      });
    },
    [assetId, basePath, issueId, navigate]
  );

  if (isLoading) {
    return (
      <View style={filesSharedStyles.container}>
        <LoadingIndicator flexContainer />
      </View>
    );
  }

  if (!workspaceAsset) return null;

  return (
    <View style={filesSharedStyles.container}>
      <ScrollView
        showsVerticalScrollIndicator={true}
        style={filesSharedStyles.scrollView}
      >
        {filteredFiles.map((file: MediaExtended) => (
          <MediaListItem
            key={file.id}
            media={file}
            invalidateQuery={invalidateQuery}
            useNewCropCircle
            onClickMedia={handleMediaClick}
          />
        ))}
      </ScrollView>
    </View>
  );
};
